import React, {
	memo,
	useCallback,
	useMemo,
	useState,
	FunctionComponent,
	useEffect,
	useRef,
} from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Grid } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'
import cuid from 'cuid'
import { useHistory } from 'react-router-dom'

import styles from './styles'
import { EJobActionTypes } from 'models'
import { StoreValue } from 'store'
import Header from './components/header'
import { Step1, Step2, Step3, Step4, CreateJobProgress } from './components'
import { EnumUserRoles } from 'utils'
import { Loading } from 'components'

const useStyles = makeStyles(styles)
const STEP_1 = 0
const STEP_2 = 1
const STEP_3 = 2
const STEP_4 = 3

const Job: FunctionComponent<any> = props => {
	const { storeDispatch, storeUserCompanyRoles, ownedCompanies, job } = props
	const {
		title,
		jobDescription,
		expiryDate,
		salary,
		skills,
		contractType,
		jobType,
		jobTypeLoc,
		seniorityLevel,
		team,
		benefits,
		locations,
		id,
	} = job
	const {
		state,
	}: {
		state?: { add?: boolean; update?: boolean; editedJob: any }
	} = useLocation()
	const { companySlug }: { companySlug?: string } = useParams()
	const history = useHistory()
	const classes = useStyles()
	const { t, i18n } = useTranslation()
	const [activeStep, setActiveStep] = useState(0)
	const [skipped, setSkipped] = useState(new Set<number>())
	const [basicInformationStep, setBasicInformationStep] = useState(false)
	const [detailsStep, setDetailsStep] = useState(false)
	const [teamsStep, setTeamsStep] = useState(false)
	const [postJob /*setPostJob*/] = useState(false)
	const [highlightProgressBar, setHighlightProgressBar] = useState(false)
	const intervalId = useRef<any>()

	const userCompanies = useMemo(() => {
		let jobPublisherCompanies = []
		if (storeUserCompanyRoles && storeUserCompanyRoles.length > 0) {
			// get all the companies where the current user has 'job publisher' permission
			jobPublisherCompanies = storeUserCompanyRoles
				.filter((item: any) => {
					return item.roles.includes(EnumUserRoles.JOB_PUBLISHER)
				})
				.map((item: any) => {
					return item.company
				})
		}
		if (ownedCompanies) {
			jobPublisherCompanies = [
				...ownedCompanies,
				...jobPublisherCompanies,
			]
		}

		return jobPublisherCompanies ? jobPublisherCompanies : []
	}, [storeUserCompanyRoles, ownedCompanies])

	const activeCompany = userCompanies.find((item: any) => {
		return item.slug === companySlug
	})

	const isStepOptional = useCallback((step: number) => {
		return step === 1
	}, [])

	const isStepSkipped = useCallback(
		(step: number) => {
			return skipped.has(step)
		},
		[skipped],
	)

	const handleBack = useCallback(() => {
		setActiveStep(prevActiveStep => prevActiveStep - 1)
	}, [])

	const handleChoseCompanyJob = useCallback(
		company => {
			let stateData =
				state && state.add
					? { add: true }
					: { update: true, editedJob: state && state.editedJob }

			history.replace({
				pathname: `/${i18n.language}/${company.slug}/jobs/${
					state && !state.add
						? `edit/${stateData.editedJob.slug}/${stateData.editedJob.id}`
						: 'create'
				}`,
				state: stateData,
			})
			storeDispatch({
				type: EJobActionTypes.setCompanyJob,
				jobCompany: company,
			})
			storeDispatch({
				type: EJobActionTypes.setSearchForDefaultCompanyOffices,
				value: true,
			})
			storeDispatch({
				type: EJobActionTypes.setSearchForDefaultCompanyTeams,
				value: true,
			})

			if (locations.length > 0) {
				storeDispatch({
					type: EJobActionTypes.setLocations,
					locations: [],
				})
			}
			if (team) {
				storeDispatch({
					type: EJobActionTypes.setTeam,
					team: null,
				})
			}

			if (activeStep === 2) {
				handleBack()
			}
		},
		[
			storeDispatch,
			activeStep,
			handleBack,
			locations,
			team,
			state,
			history,
			i18n.language,
		],
	)
	const handleNext = useCallback(() => {
		let newSkipped = skipped
		if (isStepSkipped(activeStep)) {
			newSkipped = new Set(newSkipped.values())
			newSkipped.delete(activeStep)
		}

		setActiveStep(prevActiveStep => prevActiveStep + 1)
		setSkipped(newSkipped)
	}, [activeStep, isStepSkipped, skipped])

	const handleSkip = useCallback(() => {
		if (!isStepOptional(activeStep)) {
			// You probably want to guard against something like this,
			// it should never occur unless someone's actively trying to break something.
			throw new Error("You can't skip a step that isn't optional.")
		}

		setActiveStep(prevActiveStep => prevActiveStep + 1)
		setSkipped(prevSkipped => {
			const newSkipped = new Set(prevSkipped.values())
			newSkipped.add(activeStep)
			return newSkipped
		})
	}, [isStepOptional, activeStep, setActiveStep, setSkipped])

	const resetSteps = useCallback(() => {
		setActiveStep(0)
	}, [setActiveStep])

	const scrollStep = useCallback(() => {
		if (window.pageYOffset === 0) {
			clearInterval(intervalId.current)
		}
		window.scroll(0, window.pageYOffset - 30)
	}, [intervalId])

	const scrollToTop = useCallback(() => {
		let intId = setInterval(scrollStep, 20)
		intervalId.current = intId
	}, [scrollStep])

	const handleEditJobDetails = useCallback(() => {
		scrollToTop()
		setHighlightProgressBar(true)
	}, [scrollToTop])

	const handleSelectedStep = useCallback(
		(number: number) => {
			setActiveStep(number)
		},
		[setActiveStep],
	)

	const renderCurrentStep = useCallback(() => {
		switch (activeStep) {
			case STEP_1:
				return <Step1 handleNext={handleNext} />
			case STEP_2:
				return (
					<Step2
						handleNext={handleNext}
						handleBack={handleBack}
						handleSkip={handleSkip}
					/>
				)
			case STEP_3:
				return (
					<Step3
						handleNext={handleNext}
						handleBack={handleBack}
						handleSkip={handleSkip}
					/>
				)
		}
	}, [activeStep, handleBack, handleNext, handleSkip])

	useEffect(() => {
		// let checkSkills = skills.length > 0
		let checkJobTechnicalDetails =
			skills.length > 0 &&
			benefits &&
			locations.length > 0 &&
			contractType &&
			jobType &&
			jobTypeLoc &&
			seniorityLevel
		if (title && jobDescription && expiryDate) {
			setBasicInformationStep(true)
		} else {
			setBasicInformationStep(false)
		}

		if (checkJobTechnicalDetails) {
			setDetailsStep(true)
		} else {
			setDetailsStep(false)
		}
		if (team) {
			setTeamsStep(true)
		} else {
			setTeamsStep(false)
		}
	}, [
		title,
		jobDescription,
		expiryDate,
		salary,
		contractType,
		jobType,
		jobTypeLoc,
		seniorityLevel,
		skills,
		team,
		benefits,
		locations,
	])

	useEffect(() => {
		if (highlightProgressBar && activeStep !== STEP_4) {
			setHighlightProgressBar(false)
		}
	}, [highlightProgressBar, activeStep])

	useEffect(() => {
		if (!id) {
			if (state && state.update && state.editedJob) {
				storeDispatch({
					type: EJobActionTypes.setJob,
					dataJob: state.editedJob,
				})
			} else if (activeCompany) {
				storeDispatch({
					type: EJobActionTypes.initState,
					initState: { id: cuid(), jobCompany: activeCompany },
				})
			}
		}
	}, [state, storeDispatch, activeCompany, id])

	// on unmount, clean job state
	useEffect(() => {
		return () => {
			storeDispatch({
				type: EJobActionTypes.resetState,
			})
		}
	}, [storeDispatch])

	return !id ? (
		// render the main components only if the job has been fetched from server or an id was generated for a new job
		<Loading size={42} />
	) : (
		<>
			<Grid container={true} justify="center" direction="row">
				<Grid className={classes.jobRoot}>
					<Header
						companies={userCompanies}
						activeCompany={activeCompany}
						handleChoseCompanyJob={handleChoseCompanyJob}
					/>
					{
						<Grid item className={classes.progressBarContainer}>
							{highlightProgressBar && (
								<div
									className={classes.progressBarContainerText}
								>
									{t(
										'Choose the step you want to change from the ones below.',
									)}
								</div>
							)}
							<CreateJobProgress
								basicInformationStep={basicInformationStep}
								detailsStep={detailsStep}
								teamsStep={teamsStep}
								postJob={postJob}
								handleSelectedStep={handleSelectedStep}
								activeStep={activeStep}
								highlightProgressBar={highlightProgressBar}
							/>
						</Grid>
					}
					{activeStep !== STEP_4 && (
						<Grid
							container
							justify="center"
							spacing={2}
							className={classes.jobContainer}
						>
							<Grid item xs={12}>
								{renderCurrentStep()}
							</Grid>
						</Grid>
					)}

					{activeStep === STEP_4 && (
						<Step4
							resetSteps={resetSteps}
							handleEditJobDetails={handleEditJobDetails}
						/>
					)}
				</Grid>
			</Grid>
		</>
	)
}

const JobCreation = (props: any) => (
	<StoreValue
		keys={[
			'user.companyRoles|storeUserCompanyRoles',
			'user.profile.ownedCompanies',
			'job',
			'storeDispatch',
		]}
	>
		<Job {...props} />
	</StoreValue>
)
export default memo(JobCreation)
